import { memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiMenu, MiTemplateBadge } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  DATE_TIME_FORMATS,
  DEFAULT_COMMUNITY_COLORS,
} from "@miplatform/constants";
import { IAssignment, OrderStatus } from "@miplatform/types";
import { formatDate } from "@miplatform/utils";
import { mdiMinus } from "@mdi/js";
import { ButtonProps, Typography, useTheme } from "@mui/material";
import { Box } from "@mui/system";

interface AssignmentCardProps {
  assignment: IAssignment;
  isAssignDisabled?: boolean;
  isDisabled?: boolean;
  onAssign?: (assignmentId: string) => void;
  onUnassign?: (assignmentId: string) => void;
}

export const AssignmentCard = memo(
  ({
    assignment,
    isAssignDisabled,
    isDisabled,
    onAssign,
    onUnassign,
  }: AssignmentCardProps) => {
    const theme = useTheme();
    const { home, order } = assignment;
    const {
      orderTemplateAbbreviation,
      orderTemplateColor,
      orderTemplateName,
      confirmedScheduleDate,
      effectiveScheduledDate,
    } = order ?? {};
    const templateAbbreviation =
      orderTemplateAbbreviation || orderTemplateName?.slice(0, 3);
    const { address, builderShortName, communityName } = home ?? {};
    const { addressLine1, city } = address ?? {};

    const headerColor = useMemo(() => {
      const { communityName = "" } = home || {};
      const colors = DEFAULT_COMMUNITY_COLORS;

      if (communityName) {
        const colorIndex =
          Array.from(communityName).reduce(
            (a, i) => a + (i.codePointAt(0) || 0),
            0
          ) % colors.length;
        return colors[colorIndex];
      }

      return theme.palette.bluegrey.light;
    }, [home?.communityName]);

    const { classes } = useStyles({
      isClickEnabled: !isAssignDisabled,
      status: order?.orderStatus,
      isDisabled,
      headerColor,
    });

    return (
      <Box
        className={classes.card}
        onClick={
          isAssignDisabled || isDisabled
            ? undefined
            : () => onAssign?.(assignment.id as string)
        }
      >
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <MiTemplateBadge
              title={templateAbbreviation}
              backgroundColor={orderTemplateColor}
            />
            <Typography fontSize={9} noWrap>
              {communityName}
            </Typography>
          </Box>
          <Box className={classes.headerRight}>
            {onUnassign && assignment?.assigneeId && (
              <MiButton
                aria-label="Unassign"
                size={BUTTON_SIZES.SMALL as ButtonProps["size"]}
                variant={BUTTON_VARIANTS.CONTAINED}
                color={BUTTON_COLORS.ERROR}
                className={classes.removeButton}
                startIcon={{
                  path: mdiMinus,
                  iconSize: 0.45,
                  iconColor: theme.palette.common.white,
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onUnassign?.(assignment.id as string);
                }}
              />
            )}
            <Typography
              fontSize={9}
              fontWeight={theme.fontWeight.semiBold}
              color={theme.palette.grey.medium}
              fontStyle="italic"
              pr={0.25}
              noWrap
            >
              {builderShortName}
            </Typography>
            <MiMenu condensed disabled={isDisabled} data={[]} />
          </Box>
        </Box>
        <Box className={classes.body}>
          <Box className={classes.row}>
            <Typography
              fontSize={9}
              textOverflow="ellipsis"
              fontWeight={theme.fontWeight.bold}
              noWrap
            >
              {addressLine1}
            </Typography>
            <Typography
              fontSize={9}
              color={theme.palette.grey.medium}
              fontStyle="italic"
              pr={0.25}
              maxWidth={100}
            >
              {city}
            </Typography>
          </Box>
          <Box className={classes.row}>
            <Typography
              fontSize={8}
              textOverflow="ellipsis"
              color={theme.palette.grey.medium}
              noWrap
            >
              Scheduled{" "}
              <Typography
                component="span"
                fontStyle="italic"
                fontSize="inherit"
                color="inherit"
              >
                {effectiveScheduledDate
                  ? formatDate(
                      effectiveScheduledDate,
                      DATE_TIME_FORMATS.FORMAT_MM_DD_YY
                    )
                  : ""}
              </Typography>
            </Typography>
            <Typography
              fontSize={8}
              color={theme.palette.grey.medium}
              pr={0.25}
              noWrap
              maxWidth={100}
            >
              Confirmed{" "}
              <Typography
                component="span"
                fontStyle="italic"
                fontSize="inherit"
                color="inherit"
              >
                {confirmedScheduleDate
                  ? formatDate(
                      confirmedScheduleDate,
                      DATE_TIME_FORMATS.FORMAT_MM_DD_YY
                    )
                  : ""}
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
);

const useStyles = makeStyles<{
  isClickEnabled: boolean;
  isDisabled?: boolean;
  status?: OrderStatus;
  headerColor?: string;
}>()((theme, { isClickEnabled, status, headerColor, isDisabled }) => {
  return {
    card: {
      border: `1px solid ${theme.palette.grey.accent}`,
      borderRadius: 2,
      backgroundColor: theme.palette.common.white,
      overflow: "hidden",
      width: 174,
      minHeight: 46,
      ...(status === OrderStatus.Complete && {
        backgroundColor: theme.palette.success.light,
      }),
      ...(isClickEnabled &&
        !isDisabled && {
          cursor: "pointer",
        }),
      ...(isDisabled && {
        opacity: 0.2,
      }),
    },
    header: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(0.5),
      backgroundColor: headerColor,
      height: theme.spacing(4),
    },
    body: {
      padding: theme.spacing(0.5),
    },
    row: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      gap: theme.spacing(1),
      minWidth: 0,
    },
    headerLeft: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: theme.spacing(1),
      minWidth: 0,
      flex: 1,
    },
    headerRight: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: theme.spacing(0.5),
      flexShrink: 0,
      minWidth: 0,
    },
    removeButton: {
      "&&": {
        flexShrink: 0,
        width: 14,
        height: 14,
        minWidth: 14,
        minHeight: 14,
        padding: 0,
        borderRadius: "50%",
        "&:hover": {
          backgroundColor: theme.palette.error.dark,
        },
        "& .MuiButton-startIcon": {
          margin: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
    },
  };
});

export interface IWorkOrderCardStyleProps {
  borderColor?: string;
  headerColor?: string;
  backgroundColor?: string;
  borderStyle?: string;
  minimized?: boolean;
  isHighlighted?: boolean;
  loading?: boolean;
}
